import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { mockCategories } from '../../data/mockData';

const CustomerCategories: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [categories] = useState(mockCategories);

  const handleBack = () => {
    setCurrentScreen('customer-home');
  };

  const handleCategoryClick = (categoryName: string) => {
    // Navigate to products with category filter
    setCurrentScreen('customer-products');
    // In a real app, you would pass the category filter
  };

  const getProductCount = (categoryName: string) => {
    return products.filter(product => 
      product.category.toLowerCase().includes(categoryName.toLowerCase())
    ).length;
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const CategoryCard: React.FC<{ category: any; index: number }> = ({ category, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onClick={() => handleCategoryClick(category.name)}
      className="bg-slate-800 rounded-lg p-6 border border-slate-700 cursor-pointer hover:bg-slate-700 transition-colors duration-200"
    >
      <div className="flex items-center space-x-4">
        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-purple-500 rounded-lg flex items-center justify-center text-2xl">
          {category.icon}
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold text-lg">{category.name}</h3>
          <p className="text-slate-400 text-sm mb-2">{category.description}</p>
          <div className="flex items-center space-x-4">
            <span className="text-teal-400 text-sm font-medium">
              {getProductCount(category.name)} products
            </span>
            <span className="text-slate-500 text-xs">Tap to browse</span>
          </div>
        </div>
        <div className="text-slate-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <button
          onClick={handleBack}
          className="p-2 text-slate-400 hover:text-white mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-white">Categories</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Search categories..."
          />
        </motion.div>

        {/* Categories Grid */}
        <div className="space-y-4">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category, index) => (
              <CategoryCard key={category.id} category={category} index={index} />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📁</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No Categories Found</h3>
              <p className="text-slate-400 text-sm">
                Try adjusting your search terms
              </p>
            </div>
          )}
        </div>

        {/* Popular Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Popular Categories</h3>
          <div className="grid grid-cols-2 gap-3">
            {categories.slice(0, 4).map((category, index) => (
              <motion.button
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-slate-700 hover:bg-slate-600 rounded-lg p-4 text-center transition-colors duration-200"
              >
                <div className="text-2xl mb-2">{category.icon}</div>
                <p className="text-white text-sm font-medium">{category.name}</p>
                <p className="text-slate-400 text-xs">{getProductCount(category.name)} items</p>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerCategories;
